import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useGuest } from '../contexts/GuestContext';
import { useLanguage } from '../contexts/LanguageContext';
import '../styles/guest.css';

function GuestPage() {
    const { guestGroup, logout, submitRSVP, loading } = useGuest();
    const { t, l } = useLanguage();
    const [members, setMembers] = useState([]);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        if (!loading && !guestGroup) {
            navigate(l('/guest/login'));
        } else if (guestGroup) {
            setMembers(guestGroup.members);
        }
    }, [guestGroup, loading, navigate]);

    const handleMemberChange = (index, field, value) => {
        const updated = [...members];
        updated[index][field] = value;
        setMembers(updated);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage('');

        const result = await submitRSVP(members);
        
        if (result.success) {
            setMessage(t('rsvpSuccess'));
        } else {
            setMessage(t('rsvpError'));
        }
        
        setSaving(false);
    };

    const handleLogout = () => {
        logout();
        navigate(l('/'));
    };

    if (loading || !guestGroup) {
        return <div className="guest-page">{t('loadingPhotos')}</div>;
    }

    return (
        <div className="guest-page">
            <div className="guest-header">
                <h1>{t('welcome')}, {guestGroup.groupName}!</h1>
                <button onClick={handleLogout} className="logout-btn">{t('logout')}</button>
            </div>

            <div className="guest-content">
                <section className="rsvp-section">
                    <h2>{t('rsvp')}</h2>
                    {guestGroup.rsvpSubmitted && (
                        <p className="rsvp-status">
                            {t('submittedOn', new Date(guestGroup.rsvpSubmittedAt).toLocaleDateString())}
                        </p>
                    )}
                    
                    <form onSubmit={handleSubmit} className="rsvp-form">
                        {members.map((member, index) => (
                            <div key={member.id} className="member-card">
                                <h3>{member.name}</h3>
                                
                                <div className="form-group">
                                    <label>{t('attending')}</label>
                                    <div className="radio-group">
                                        <label>
                                            <input
                                                type="radio"
                                                name={`attending-${member.id}`}
                                                checked={member.attending === true}
                                                onChange={() => handleMemberChange(index, 'attending', true)}
                                            />
                                            {t('yes')}
                                        </label>
                                        <label>
                                            <input
                                                type="radio"
                                                name={`attending-${member.id}`}
                                                checked={member.attending === false}
                                                onChange={() => handleMemberChange(index, 'attending', false)}
                                            />
                                            {t('no')}
                                        </label>
                                    </div>
                                </div>

                                {member.attending && (
                                    <div className="form-group">
                                        <label>{t('dietary')}</label>
                                        <textarea
                                            value={member.dietaryRequirements}
                                            onChange={(e) => handleMemberChange(index, 'dietaryRequirements', e.target.value)}
                                            placeholder={t('dietaryPlaceholder')}
                                        />
                                    </div>
                                )}

                                {member.plusOne && (
                                    <div className="form-group">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={member.bringingPlusOne || false}
                                                onChange={(e) => handleMemberChange(index, 'bringingPlusOne', e.target.checked)}
                                            />
                                            {t('bringingPlusOne')}
                                        </label>
                                    </div>
                                )}
                            </div>
                        ))}

                        {message && <div className={`message ${message === t('rsvpSuccess') ? 'success' : 'error'}`}>{message}</div>}

                        <button type="submit" disabled={saving} className="submit-btn">
                            {saving ? t('saving') : t('submitRsvp')}
                        </button>
                    </form>
                </section>

                <section className="album-section">
                    <h2>{t('yourAlbum')}</h2>
                    <p>{t('viewAndUpload')}</p>
                    <Link to={l(`/gallery/${guestGroup.albumId}`)} className="view-album-btn">
                        {t('viewAlbum')}
                    </Link>
                </section>

                <section className="public-gallery-section">
                    <h2>{t('publicGallery')}</h2>
                    <p>{t('viewPhotosAll')}</p>
                    <Link to={l('/gallery')} className="view-gallery-btn">
                        {t('viewGallery')}
                    </Link>
                </section>
            </div>
        </div>
    );
}

export default GuestPage;
